import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Download, User, BookOpen, BarChart3, TrendingUp, Award, 
  Filter, ChevronDown, FileText, Calendar, Users
} from 'lucide-react';
import api from '../utils/api';

const StudentAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  
  // Data states
  const [students, setStudents] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null);
  
  // Filter states
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  
  // Filter options (from API)
  const [filterOptions, setFilterOptions] = useState({
    assessment_types: [],
    sections: [],
    subjects: [],
    terms: [],
    boards: []
  });

  // Fetch students on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [studentsRes, schoolRes] = await Promise.all([
          api.get('/school-admin/students'),
          api.get('/school/info')
        ]);
        setStudents(studentsRes.data);
        setSchoolInfo(schoolRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Try alternate endpoint
        try {
          const studentsRes = await api.get('/admin/students');
          setStudents(studentsRes.data);
        } catch (e) {
          console.error('Error fetching students:', e);
        }
      }
    };
    fetchInitialData();
  }, []);

  // Fetch analytics when student or filters change
  const fetchAnalytics = useCallback(async () => {
    if (!selectedStudent) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBoard) params.append('board_id', selectedBoard);
      if (selectedSection) params.append('section_id', selectedSection);
      if (selectedAssessmentType) params.append('assessment_type_code', selectedAssessmentType);
      if (selectedSubject) params.append('subject_config_id', selectedSubject);
      if (selectedTerm) params.append('term_id', selectedTerm);
      
      const queryString = params.toString();
      const url = `/igcse/analytics/student/${selectedStudent}${queryString ? '?' + queryString : ''}`;
      
      const response = await api.get(url);
      setAnalyticsData(response.data);
      
      // Update filter options from response
      if (response.data.filters) {
        setFilterOptions(response.data.filters);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedStudent, selectedBoard, selectedSection, selectedAssessmentType, selectedSubject, selectedTerm]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Get grade color
  const getGradeColor = (grade) => {
    const colors = {
      'A*': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'A': 'bg-green-100 text-green-800 border-green-200',
      'B': 'bg-blue-100 text-blue-800 border-blue-200',
      'C': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'D': 'bg-orange-100 text-orange-800 border-orange-200',
      'E': 'bg-red-100 text-red-800 border-red-200',
      'U': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[grade] || 'bg-gray-100 text-gray-800';
  };

  // Get AO bar color
  const getAOBarColor = (percentage) => {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 60) return 'bg-blue-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Get assessment type badge color
  const getAssessmentTypeBadgeColor = (code) => {
    const colors = {
      'CT': 'bg-purple-100 text-purple-700',
      'UT': 'bg-blue-100 text-blue-700',
      'TE': 'bg-green-100 text-green-700',
      'ME': 'bg-orange-100 text-orange-700'
    };
    return colors[code] || 'bg-gray-100 text-gray-700';
  };

  const handlePrint = () => {
    window.print();
  };

  const clearFilters = () => {
    setSelectedBoard('');
    setSelectedSection('');
    setSelectedAssessmentType('');
    setSelectedSubject('');
    setSelectedTerm('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Student Analytics Dashboard</h1>
                <p className="text-sm text-gray-500">Comprehensive assessment performance analysis</p>
              </div>
            </div>
            {analyticsData && (
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                data-testid="download-report-btn"
              >
                <Download className="w-5 h-5" />
                Print / Download
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters Section */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6 print:hidden">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
            {(selectedBoard || selectedSection || selectedAssessmentType || selectedSubject || selectedTerm) && (
              <button
                onClick={clearFilters}
                className="ml-auto text-sm text-indigo-600 hover:text-indigo-800"
              >
                Clear all filters
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Student Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Student</label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                data-testid="student-select"
              >
                <option value="">Select Student</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Board Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Board</label>
              <select
                value={selectedBoard}
                onChange={(e) => setSelectedBoard(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                data-testid="board-select"
              >
                <option value="">All Boards</option>
                {filterOptions.boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {board.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Section Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Section</label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                data-testid="section-select"
              >
                <option value="">All Sections</option>
                {filterOptions.sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Assessment Type Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Assessment Type</label>
              <select
                value={selectedAssessmentType}
                onChange={(e) => setSelectedAssessmentType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                data-testid="assessment-type-select"
              >
                <option value="">All Types</option>
                {filterOptions.assessment_types.map((type) => (
                  <option key={type.id} value={type.code}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                data-testid="subject-select"
              >
                <option value="">All Subjects</option>
                {filterOptions.subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Term Dropdown */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Term</label>
              <select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                data-testid="term-select"
              >
                <option value="">All Terms</option>
                {filterOptions.terms.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading analytics...</p>
          </div>
        )}

        {/* No Student Selected State */}
        {!loading && !selectedStudent && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Student</h3>
            <p className="text-gray-500">Choose a student to view their comprehensive analytics.</p>
          </div>
        )}

        {/* No Results State */}
        {!loading && selectedStudent && analyticsData && analyticsData.total_assessments === 0 && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Results Found</h3>
            <p className="text-gray-500">No assessment results match the selected filters.</p>
          </div>
        )}

        {/* Analytics Content */}
        {!loading && analyticsData && analyticsData.total_assessments > 0 && (
          <div ref={reportRef} className="space-y-6">
            {/* Student Header Card */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {(schoolInfo?.logo_url || analyticsData.school?.logo_url) && (
                    <img
                      src={schoolInfo?.logo_url || analyticsData.school?.logo_url}
                      alt="School Logo"
                      className="w-16 h-16 object-contain bg-white rounded-lg p-1"
                    />
                  )}
                  <div>
                    <p className="text-indigo-200 text-sm">
                      {schoolInfo?.name || analyticsData.school?.name || 'School'}
                    </p>
                    <h2 className="text-2xl font-bold">{analyticsData.student?.name}</h2>
                    <p className="text-indigo-200">{analyticsData.student?.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-indigo-200 text-sm">Overall Average</p>
                  <p className="text-4xl font-bold">{analyticsData.overall_average?.toFixed(1)}%</p>
                  <p className="text-indigo-200">{analyticsData.total_assessments} Assessments</p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Assessments</p>
                    <p className="text-xl font-bold text-gray-900">{analyticsData.total_assessments}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BookOpen className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Subjects</p>
                    <p className="text-xl font-bold text-gray-900">
                      {Object.keys(analyticsData.results_by_subject || {}).length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Average</p>
                    <p className="text-xl font-bold text-gray-900">{analyticsData.overall_average?.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Award className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Top Grade</p>
                    <p className="text-xl font-bold text-gray-900">
                      {analyticsData.all_results?.reduce((best, r) => {
                        const grades = ['A*', 'A', 'B', 'C', 'D', 'E', 'U'];
                        const bestIdx = grades.indexOf(best);
                        const currentIdx = grades.indexOf(r.grade);
                        return currentIdx < bestIdx ? r.grade : best;
                      }, 'U') || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* AO Performance (if tracking enabled for any result) */}
            {(analyticsData.ao_summary?.ao1?.max > 0 || 
              analyticsData.ao_summary?.ao2?.max > 0 || 
              analyticsData.ao_summary?.ao3?.max > 0) && (
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Assessment Objectives Performance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* AO1 */}
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">AO1: Knowledge & Understanding</span>
                      <span className="text-xl font-bold text-blue-600">
                        {analyticsData.ao_summary?.ao1?.percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${getAOBarColor(analyticsData.ao_summary?.ao1?.percentage || 0)}`}
                        style={{ width: `${Math.min(analyticsData.ao_summary?.ao1?.percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      {analyticsData.ao_summary?.ao1?.marks?.toFixed(1) || 0} / {analyticsData.ao_summary?.ao1?.max?.toFixed(1) || 0} marks
                    </p>
                  </div>

                  {/* AO2 */}
                  <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-800">AO2: Application</span>
                      <span className="text-xl font-bold text-green-600">
                        {analyticsData.ao_summary?.ao2?.percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${getAOBarColor(analyticsData.ao_summary?.ao2?.percentage || 0)}`}
                        style={{ width: `${Math.min(analyticsData.ao_summary?.ao2?.percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      {analyticsData.ao_summary?.ao2?.marks?.toFixed(1) || 0} / {analyticsData.ao_summary?.ao2?.max?.toFixed(1) || 0} marks
                    </p>
                  </div>

                  {/* AO3 */}
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-800">AO3: Analysis & Evaluation</span>
                      <span className="text-xl font-bold text-purple-600">
                        {analyticsData.ao_summary?.ao3?.percentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${getAOBarColor(analyticsData.ao_summary?.ao3?.percentage || 0)}`}
                        style={{ width: `${Math.min(analyticsData.ao_summary?.ao3?.percentage || 0, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-purple-600 mt-2">
                      {analyticsData.ao_summary?.ao3?.marks?.toFixed(1) || 0} / {analyticsData.ao_summary?.ao3?.max?.toFixed(1) || 0} marks
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Results by Subject */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                  Subject-wise Results
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="results-table">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Assessment</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Subject</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Score</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Percentage</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700">Grade</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Section</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Term</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.all_results?.map((result, index) => (
                      <tr key={result.id || index} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{result.assessment_name}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getAssessmentTypeBadgeColor(result.assessment_type_code)}`}>
                            {result.assessment_type_code}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-900">{result.subject_name}</p>
                          <p className="text-xs text-gray-500">{result.subject_code}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {result.simple_score ? (
                            <span className="font-medium">
                              {result.simple_score.marks_obtained}/{result.simple_score.max_marks}
                            </span>
                          ) : result.weighted_percentage ? (
                            <span className="text-xs text-gray-500">Paper-based</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-indigo-600">{result.percentage?.toFixed(1)}%</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold border ${getGradeColor(result.grade)}`}>
                            {result.grade}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {result.section_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {result.term_name || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Subject Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(analyticsData.results_by_subject || {}).map(([subjectId, data]) => {
                const avgPercentage = data.results.reduce((sum, r) => sum + (r.percentage || 0), 0) / data.results.length;
                const assessmentCount = data.results.length;
                
                return (
                  <div key={subjectId} className="bg-white rounded-xl p-4 shadow-sm border">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">{data.subject_name}</h4>
                        <p className="text-xs text-gray-500">{data.subject_code}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-indigo-600">{avgPercentage.toFixed(1)}%</p>
                        <p className="text-xs text-gray-500">Average</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full ${getAOBarColor(avgPercentage)}`}
                        style={{ width: `${Math.min(avgPercentage, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500">{assessmentCount} assessment{assessmentCount !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StudentAnalyticsDashboard;
